/**
 * Mock therapist data for demonstration purposes
 * Used when real therapist data is not available
 */

export interface MockTherapist {
  id: string;
  profile: {
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
  city: string;
  country: string;
  is_online_available: boolean;
  is_in_person_available: boolean;
  rating: number;
  hourly_rate: number;
  currency: string;
  languages: string[];
  therapist_specialties: Array<{
    specialty: {
      name: string;
    };
  }>;
  bio?: string;
  years_experience?: number;
}

export const generateMockTherapists = (city: string, country: string, count: number = 10): MockTherapist[] => {
  const firstNames = [
    'María', 'Carlos', 'Ana', 'David', 'Laura', 'Miguel', 'Sofia', 'Pablo', 'Elena', 'Jorge',
    'Isabel', 'Javier', 'Carmen', 'Antonio', 'Beatriz', 'Fernando', 'Lucia', 'Rafael', 'Monica', 'Diego'
  ];
  
  const lastNames = [
    'García', 'Rodríguez', 'Martínez', 'López', 'González', 'Fernández', 'Sánchez', 'Pérez', 'Martín', 'Gómez',
    'Ruiz', 'Díaz', 'Hernández', 'Moreno', 'Jiménez', 'Álvarez', 'Romero', 'Torres', 'Ramírez', 'Vargas'
  ];
  
  const specialties = [
    'Clinical Psychology',
    'Anxiety & Depression Specialist',
    'Couples Therapy',
    'Child & Adolescent Psychology',
    'Trauma & PTSD Specialist',
    'Cognitive Behavioral Therapy (CBT)',
    'Family Therapy',
    'Addiction Counseling',
    'Stress Management',
    'Neuropsychology',
    'EMDR Therapy',
    'Mindfulness-Based Therapy',
    'Grief Counseling',
    'OCD Treatment',
    'Eating Disorders Specialist'
  ];

  const languageSets = [
    ['English', 'Spanish'],
    ['English', 'Spanish', 'French'],
    ['English', 'German'],
    ['English', 'Spanish', 'Portuguese'],
    ['English', 'Italian'],
    ['Spanish', 'Catalan'],
    ['English', 'Spanish', 'German'],
    ['English'],
    ['English', 'Spanish', 'Italian'],
    ['English', 'French']
  ];

  const bios = [
    'Experienced psychotherapist specializing in evidence-based treatments with over 10 years helping clients achieve their mental health goals.',
    'Compassionate therapist dedicated to creating a safe, non-judgmental space for healing and personal growth.',
    'Licensed clinical psychologist with expertise in treating anxiety, depression, and relationship issues.',
    'Certified specialist in trauma-informed care and EMDR therapy, committed to helping clients overcome past experiences.',
    'Holistic approach to mental health, integrating mindfulness and cognitive-behavioral techniques.',
    'Passionate about empowering individuals and couples to develop healthy coping strategies and improve their relationships.',
    'Board-certified psychologist with extensive training in neuropsychology and cognitive assessment.',
    'Expert in child and adolescent development, helping young people navigate emotional and behavioral challenges.',
    'Specialized in addiction recovery and co-occurring mental health disorders with a focus on long-term wellness.',
    'Dedicated to supporting clients through life transitions and helping them discover their inner strength.'
  ];

  const mockTherapists: MockTherapist[] = [];

  for (let i = 0; i < count; i++) {
    const firstName = firstNames[i % firstNames.length];
    const lastName = lastNames[i % lastNames.length];
    const specialty = specialties[i % specialties.length];
    const languages = languageSets[i % languageSets.length];
    const bio = bios[i % bios.length];
    
    // Vary the rates based on specialty and experience
    const baseRate = 60 + (i * 5);
    const rating = 4.5 + (Math.random() * 0.5);
    const yearsExperience = 3 + (i % 15);
    
    mockTherapists.push({
      id: `mock-therapist-${i + 1}`,
      profile: {
        first_name: firstName,
        last_name: lastName,
        avatar_url: `https://api.dicebear.com/7.x/avataaars/svg?seed=${firstName}${lastName}`
      },
      city: city,
      country: country,
      is_online_available: i % 3 !== 0, // Most are online
      is_in_person_available: i % 2 === 0, // Half offer in-person
      rating: Math.round(rating * 10) / 10,
      hourly_rate: baseRate,
      currency: 'USD',
      languages: languages,
      therapist_specialties: [
        {
          specialty: {
            name: specialty
          }
        }
      ],
      bio: bio,
      years_experience: yearsExperience
    });
  }

  return mockTherapists;
};
